
import { useState } from 'react';
import {
  exportSectionToCSV,
  exportFullReportToCSV,
  downloadCSV,
} from './export/csvExport';
import {
  exportSectionToExcel,
  exportFullReportToExcel,
} from './export/excelExport';
import {
  exportSectionScreenshotPDF,
} from './export/pdfScreenshotExport';
import {
  exportSuccessCriteriaPDF,
  exportCommentBanksPDF,
  exportRubricsPDF,
  exportShortAnswerPDF,
  exportFullReportDataPDF,
} from './export/pdfDataExport';
import {
  generateSectionFilename,
  generateReportFilename,
  SECTIONS,
} from './export/fileNaming';

export function DownloadManager({
  section,
  data,
  csvData,
  chartRef,
  allData,
  isFullReport = false,
  isModal = false,
  filterData,
  children,
}) {
  const [loading, setLoading] = useState(false);
  const [loadingFormat, setLoadingFormat] = useState(null);

  const handleCSVExport = async () => {
    setLoading(true);
    setLoadingFormat('csv');

    try {
      const className = filterData?.classLabels?.[0] || null;

      // Check if data is still loading
      if (isFullReport && allData?.isDataLoading) {
        alert('Data is still loading. Please wait a moment and try again.');
        setLoading(false);
        setLoadingFormat(null);
        return;
      }

      if (isFullReport) {
        const csvContent = exportFullReportToCSV(allData);
        const filename = generateReportFilename('csv', className);
        downloadCSV(csvContent, filename);
      } else {
        const filename = generateSectionFilename(section, 'csv', className);
        const dataToExport = Array.isArray(csvData) && csvData.length > 0 ? csvData : data;

        exportSectionToCSV(section, dataToExport, filename);
      }
    } catch (error) {
      console.error('❌ [CSV Export] Failed:', error);
      alert('Failed to export CSV. Please try again.');
    } finally {
      setLoading(false);
      setLoadingFormat(null);
    }
  };

  const getPdfChartCanvas = async () => {
    if (chartRef?.current) {
      const chart = chartRef.current;
      const canvas = chart.canvas || chart.ctx?.canvas || chart.chart?.canvas || null;
      if (canvas) return canvas;
    }

    if (section === SECTIONS.COMMENT_BANKS) {
      return (
        document.querySelector('.ci-comment-chart canvas') ||
        document.querySelector('.ci-modal-chart canvas') ||
        document.querySelector('[data-section="comment-banks-modal"] canvas') ||
        null
      );
    }

    return null;
  };

  const handleExcelExport = async () => {
    setLoading(true);
    setLoadingFormat('xlsx');

    try {
      const className = filterData?.classLabels?.[0] || null;

      // Check if data is still loading
      if (isFullReport && allData?.isDataLoading) {
        alert('Data is still loading. Please wait a moment and try again.');
        setLoading(false);
        setLoadingFormat(null);
        return;
      }

      if (isFullReport) {
        const filename = generateReportFilename('xlsx', className);
        await exportFullReportToExcel(allData, filename);
      } else {
        const filename = generateSectionFilename(section, 'xlsx', className);
        const dataToExport = Array.isArray(csvData) && csvData.length > 0 ? csvData : data;

        await exportSectionToExcel(section, dataToExport, filename);
      }
    } catch (error) {
      console.error('❌ [Excel Export] Failed:', error);
      alert('Failed to export Excel. Please try again.');
    } finally {
      setLoading(false);
      setLoadingFormat(null);
    }
  };

  const prepareForScreenshotExport = async () => {
    // Scroll through page to ensure all lazy-loaded content is rendered
    await new Promise((resolve) => setTimeout(resolve, 300));

    const pageElement = document.querySelector('.ci-page.class-insights-page');
    if (pageElement) {
      const scrollHeight = pageElement.scrollHeight;
      const scrollStep = 500;

      for (let scrollPos = 0; scrollPos < scrollHeight; scrollPos += scrollStep) {
        window.scrollTo(0, scrollPos);
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      window.scrollTo(0, 0);
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
  };

  const handlePDFExport = async () => {
    setLoading(true);
    setLoadingFormat('pdf');

    try {
      const className = filterData?.classLabels?.[0] || null;

      // Check if data is still loading
      if (isFullReport && allData?.isDataLoading) {
        alert('Data is still loading. Please wait a moment and try again.');
        setLoading(false);
        setLoadingFormat(null);
        return;
      }

      if (isFullReport) {
        const filename = generateReportFilename('pdf', className);

        // Get Comment Bank chart canvas for pie chart
        const commentBankChartCanvas = document.querySelector('.ci-comment-chart canvas') ||
                                        document.querySelector('.ci-modal-chart canvas') ||
                                        null;

        await exportFullReportDataPDF(allData, filename, filterData, commentBankChartCanvas);
      } else {
        const filename = generateSectionFilename(section, 'pdf', className);

        // Use data-based PDF export for all marking tools to ensure proper text display and sanitization
        if (section === SECTIONS.SUCCESS_CRITERIA) {
          await exportSuccessCriteriaPDF(data, filename, className, filterData);
        } else if (section === SECTIONS.COMMENT_BANKS) {
          const chartCanvas = await getPdfChartCanvas();
          await exportCommentBanksPDF(data, filename, className, filterData, chartCanvas);
        } else if (section === SECTIONS.RUBRICS) {
          const dataToExport = Array.isArray(csvData) && csvData.length > 0 ? csvData : data;
          // PDF export is table-only; graph view export is disabled for now.
          // let chartCanvas = null;
          // try {
          //   const { generateRubricGraphCanvas } = await import('./export/rubricGraphCanvas');
          //   chartCanvas = await generateRubricGraphCanvas(percentageStats || []);
          // } catch (error) {
          //   console.warn('📊 [PDF Export] Failed to generate rubrics chart:', error);
          // }
          await exportRubricsPDF(dataToExport, filename, className, filterData);
        } else if (section === SECTIONS.SHORT_ANSWER) {
          await exportShortAnswerPDF(data, filename, className, filterData);
        } else {
          // Use screenshot-based export for other sections
          const sectionId = isModal ? `${section}-modal` : section;
          await exportSectionScreenshotPDF(
            sectionId,
            filename,
            prepareForScreenshotExport,
            filterData
          );
        }
      }
    } catch (error) {
      let errorMessage = 'Failed to export PDF. ';
      if (error.message && error.message.includes('not found')) {
        errorMessage += 'Please wait for the content to load before exporting.';
      } else {
        errorMessage += error.message || 'Please try again or use CSV/Excel format.';
      }

      alert(errorMessage);
    } finally {
      setLoading(false);
      setLoadingFormat(null);
      window.scrollTo(0, 0);
    }
  };

  const handleExport = async (format) => {
    switch (format) {
      case 'csv':
        return handleCSVExport();
      case 'xlsx':
      case 'xls':
        return handleExcelExport();
      case 'pdf':
        return handlePDFExport();
      default:
        return null;
    }
  };

  return children({
    handleCSVExport,
    handleExcelExport,
    handlePDFExport,
    handleExport,
    loading,
    loadingFormat,
  });
}

export function useDownload({
  section,
  data,
  csvData,
  chartRef,
  allData,
  isFullReport = false,
  isModal = false,
  filterData,
}) {
  const [loading, setLoading] = useState(false);
  const [loadingFormat, setLoadingFormat] = useState(null);

  const handleCSVExport = async () => {
    setLoading(true);
    setLoadingFormat('csv');

    try {
      const className = filterData?.classLabels?.[0] || null;

      if (isFullReport) {
        const csvContent = exportFullReportToCSV(allData);
        const filename = generateReportFilename('csv', className);
        downloadCSV(csvContent, filename);
      } else {
        const filename = generateSectionFilename(section, 'csv', className);
        const dataToExport = Array.isArray(csvData) && csvData.length > 0 ? csvData : data;
        exportSectionToCSV(section, dataToExport, filename);
      }
    } catch (error) {
      alert('Failed to export CSV. Please try again.');
    } finally {
      setLoading(false);
      setLoadingFormat(null);
    }
  };

  const handleExcelExport = async () => {
    setLoading(true);
    setLoadingFormat('xlsx');

    try {
      const className = filterData?.classLabels?.[0] || null;

      if (isFullReport) {
        const filename = generateReportFilename('xlsx', className);
        await exportFullReportToExcel(allData, filename);
      } else {
        const filename = generateSectionFilename(section, 'xlsx', className);
        const dataToExport = Array.isArray(csvData) && csvData.length > 0 ? csvData : data;
        await exportSectionToExcel(section, dataToExport, filename);
      }
    } catch (error) {
      console.error('📊 [Excel Export Hook] Failed:', error);
      alert('Failed to export Excel. Please try again.');
    } finally {
      setLoading(false);
      setLoadingFormat(null);
    }
  };

  const prepareForScreenshotExport = async () => {
    // Scroll through page to ensure all lazy-loaded content is rendered
    await new Promise((resolve) => setTimeout(resolve, 300));

    const pageElement = document.querySelector('.ci-page.class-insights-page');
    if (pageElement) {
      const scrollHeight = pageElement.scrollHeight;
      const scrollStep = 500;

      for (let scrollPos = 0; scrollPos < scrollHeight; scrollPos += scrollStep) {
        window.scrollTo(0, scrollPos);
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      window.scrollTo(0, 0);
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
  };

  const handlePDFExport = async () => {
    setLoading(true);
    setLoadingFormat('pdf');

    try {
      const className = filterData?.classLabels?.[0] || null;

      // Check if data is still loading
      if (isFullReport && allData?.isDataLoading) {
        alert('Data is still loading. Please wait a moment and try again.');
        setLoading(false);
        setLoadingFormat(null);
        return;
      }

      if (isFullReport) {
        const filename = generateReportFilename('pdf', className);

        // Get Comment Bank chart canvas for pie chart
        const commentBankChartCanvas = document.querySelector('.ci-comment-chart canvas') ||
                                        document.querySelector('.ci-modal-chart canvas') ||
                                        null;

        await exportFullReportDataPDF(allData, filename, filterData, commentBankChartCanvas);
      } else {
        const filename = generateSectionFilename(section, 'pdf', className);

        // Use data-based PDF export for all marking tools to ensure proper text display and sanitization
        if (section === SECTIONS.SUCCESS_CRITERIA) {
          await exportSuccessCriteriaPDF(data, filename, className, filterData);
        } else if (section === SECTIONS.COMMENT_BANKS) {
          const chart = chartRef?.current;
          let chartCanvas = chart?.canvas || chart?.ctx?.canvas || null;

          if (!chartCanvas) {
            const pieChartCanvas = document.querySelector('.ci-comment-chart canvas') ||
                                   document.querySelector('.ci-modal-chart canvas') ||
                                   document.querySelector('[data-section="comment-banks-modal"] canvas');
            if (pieChartCanvas) {
              chartCanvas = pieChartCanvas;
            }
          }

          await exportCommentBanksPDF(data, filename, className, filterData, chartCanvas);
        } else if (section === SECTIONS.RUBRICS) {
          const dataToExport = Array.isArray(csvData) && csvData.length > 0 ? csvData : data;
          // PDF export is table-only; graph view export is disabled for now.
          // let rubricChartCanvas = null;
          // try {
          //   const { generateRubricGraphCanvas } = await import('./export/rubricGraphCanvas');
          //   rubricChartCanvas = await generateRubricGraphCanvas(percentageStats || []);
          // } catch (error) {
          //   console.warn('📊 [PDF Export useDownload] Failed to generate rubrics chart:', error);
          // }
          await exportRubricsPDF(dataToExport, filename, className, filterData);
        } else if (section === SECTIONS.SHORT_ANSWER) {
          await exportShortAnswerPDF(data, filename, className, filterData);
        } else {
          // Use screenshot-based export for other sections
          const sectionId = isModal ? `${section}-modal` : section;
          await exportSectionScreenshotPDF(
            sectionId,
            filename,
            prepareForScreenshotExport,
            filterData
          );
        }
      }
    } catch (error) {
      let errorMessage = 'Failed to export PDF. ';
      if (error.message && error.message.includes('not found')) {
        errorMessage += 'Please wait for the content to load before exporting.';
      } else {
        errorMessage += error.message || 'Please try again or use CSV/Excel format.';
      }

      alert(errorMessage);
    } finally {
      setLoading(false);
      setLoadingFormat(null);
      window.scrollTo(0, 0);
    }
  };

  const handleExport = async (format) => {
    switch (format) {
      case 'csv':
        return handleCSVExport();
      case 'xlsx':
      case 'xls':
        return handleExcelExport();
      case 'pdf':
        return handlePDFExport();
      default:
        return null;
    }
  };

  return {
    handleCSVExport,
    handleExcelExport,
    handlePDFExport,
    handleExport,
    loading,
    loadingFormat,
  };
}

export { SECTIONS };
